import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { Input } from '@shared/ui/Input';
import { numToInput, parseDecimal } from '@entities/geodesy';
import { CellInput } from './ui';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** Текущее значение; `null`/NaN — пусто. */
  value: number | null;
  /** Разобранное значение на каждый ввод; пусто или мусор → `null`. */
  onValue: (next: number | null) => void;
  /** `cell` — компактное поле в таблице, `field` — обычное поле формы. */
  variant?: 'cell' | 'field';
};

/**
 * Поле десятичного числа: принимает запятую и точку, пока поле в фокусе —
 * держит текст как набран (иначе «12,» превращалось бы в «12» на лету), вне
 * фокуса показывает значение в каноническом виде.
 */
export function NumInput({ value, onValue, variant = 'field', onFocus, onBlur, ...rest }: Props) {
  const [text, setText] = useState(numToInput(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(numToInput(value));
  }, [value, focused]);

  const invalid = text.trim() !== '' && parseDecimal(text) === null;
  const common: InputHTMLAttributes<HTMLInputElement> = {
    ...rest,
    type: 'text',
    inputMode: 'decimal',
    autoComplete: 'off',
    value: text,
    'aria-invalid': invalid || undefined,
    onFocus: (e) => {
      setFocused(true);
      onFocus?.(e);
    },
    onBlur: (e) => {
      setFocused(false);
      onBlur?.(e);
    },
    onChange: (e) => {
      setText(e.target.value);
      onValue(parseDecimal(e.target.value));
    },
  };

  return variant === 'cell' ? <CellInput className="num" {...common} /> : <Input {...common} />;
}
